import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Crown, Radio, Zap } from "lucide-react";
import { api, wsUrl } from "@/lib/api";

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function BidWarsFeed() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    api.get("/feed").then(({ data }) => setEvents(data.events)).catch(() => {});
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws/feed`);
    wsRef.current = ws;
    ws.onopen = () => setLive(true);
    ws.onclose = () => setLive(false);
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "feed_init") setEvents(msg.events);
        else if (msg.type === "takeover") setEvents((prev) => [msg.event, ...prev].slice(0, 30));
      } catch {}
    };
    return () => ws.close();
  }, []);

  return (
    <div className="glass rounded-2xl p-5" data-testid="bid-wars-feed">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-400" />
          <h3 className="font-display font-bold text-lg tracking-tight">Bid Wars</h3>
        </div>
        <span className={`inline-flex items-center gap-1 text-xs ${live ? "text-emerald-400" : "text-white/30"}`}>
          <Radio className="w-3.5 h-3.5" /> {live ? "Live" : "…"}
        </span>
      </div>

      {events.length === 0 ? (
        <div className="text-sm text-white/40 py-6 text-center">No takeovers yet — place a bid to make history.</div>
      ) : (
        <div className="space-y-2 max-h-[280px] overflow-hidden">
          <AnimatePresence initial={false}>
            {events.slice(0, 8).map((e) => (
              <motion.button
                key={e.event_id}
                layout
                initial={{ opacity: 0, x: -16, backgroundColor: "rgba(16,185,129,0.14)" }}
                animate={{ opacity: 1, x: 0, backgroundColor: "rgba(255,255,255,0.03)" }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
                onClick={() => navigate(`/k/${e.slug}`)}
                data-testid={`feed-event-${e.event_id}`}
                className="w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-3 hover:bg-white/10 transition-colors"
              >
                <Crown className="w-4 h-4 text-emerald-400 shrink-0" />
                <div className="min-w-0 flex-1 text-sm">
                  <span className="font-semibold">{e.business_name}</span>
                  <span className="text-white/50"> claimed #1 in </span>
                  <span className="text-blue-400">/k/{e.slug}</span>
                </div>
                <span className="font-display font-bold text-emerald-400 text-sm tabular-nums shrink-0">{"$"}{Number(e.locked_bid_amount ?? e.effective_bid ?? 0).toFixed(0)}</span>
                <span className="text-[10px] text-white/30 shrink-0 w-14 text-right">{timeAgo(e.created_at)}</span>
              </motion.button>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
